import GsuLoader from '@/components/GsuLoader'
import IcoMoon from '@/components/IcoMoon'
import HelenSearchSection from '@/compositions/V2/HelenSearchSection'
import PartnerAssistantPromptsBlock from '@/compositions/V2/PartnerAssistantPromptsBlock'
import VODClassCardRow from '@/compositions/V2/VODClassCardRow'
import { rbp } from '@/constants/measured-scope'
import { getConfig } from '@/lib/config'
import { getCookieValue } from '@/lib/cookie'
import { PartnerAssistantPrompt, VodClassCard } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import { useState } from 'react'
import { gql, useQuery } from 'urql'
import * as style from './style'
import { setCookie } from 'cookies-next'
import VideoJSPlayer from '@/widgets/VideoJsPlayer'
import Player from 'video.js/dist/types/player'

const QUERY_GET_HOMEPAGEPROMPTS = gql`
  query GetPartnerAssistantPrompts($partnerId: ID!) {
    partnerAssistantPrompts(partnerId: $partnerId) {
      order
      label
      description
      icon
    }
  }
`

const QUERY_LEARN_HELEN_USE_CLASSES = gql`
  query LearnHelenUseClasses {
    learnHelenUseClasses {
      id
      title
      slug
      imageUrl
      maxWidthImageUrl
      guideId
      guideName
      guideAvatarUrl
      durationSeconds
      bookmarked
    }
  }
`

interface Props {
  partnerId?: string
  hideIntroVideo?: boolean
}

const HELEN_INTRO_WATCHED = 'helen_intro_watched'

const Search = (props: Props): JSX.Element => {
  const isLargeDesktopAndAbove = useMediaQuery(`only screen and (min-width: ${rbp.largeDesktop})`)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hideVideo, setHideVideo] = useState(!!props.hideIntroVideo)
  const [videoJsPlayer, setVideoJsPlayer] = useState<Player | null>(null)
  const config = getConfig()

  // Get partner ID: props first (most local), then cookie, then default
  const getPartnerIdFromCookie = (): string | null => {
    const rawPartnerCookie = getCookieValue('auth.partner')
    if (rawPartnerCookie) {
      try {
        const parsed = JSON.parse(rawPartnerCookie)
        return parsed.id || null
      } catch (err) {
        console.error('Failed to parse partner cookie:', err)
        return null
      }
    }
    return null
  }

  const partnerIdFromCookie = getPartnerIdFromCookie()
  const partnerID = props.partnerId || partnerIdFromCookie || config.publicRuntimeConfig.defaultPartnerId

  const [{ data, fetching }] = useQuery<{
    partnerAssistantPrompts: PartnerAssistantPrompt[]
  }>({
    query: QUERY_GET_HOMEPAGEPROMPTS,
    variables: { partnerId: partnerID },
    requestPolicy: 'cache-first',
  })

  const [{ data: helenClasses, fetching: fetchingHelenClasses }] = useQuery<{
    learnHelenUseClasses: VodClassCard[]
  }>({
    query: QUERY_LEARN_HELEN_USE_CLASSES,
    requestPolicy: 'cache-first',
  })

  const handlePlayVideo = (): void => {
    setIsPlaying(true)
    if (videoJsPlayer) {
      videoJsPlayer.play()
    }
  }

  const handleVideoEnd = (): void => {
    setIsPlaying(false)
    setHideVideo(true)
    setCookie(HELEN_INTRO_WATCHED, 'true', {
      path: '/',
      domain: config.publicRuntimeConfig.auth.authCookieDomain,
      maxAge: 60 * 60 * 24 * 365,
    })
  }

  const handlePlayerReady = (player: Player): void => {
    setVideoJsPlayer(player)
    player.on('ended', handleVideoEnd)
    player.on('play', () => setIsPlaying(true))
    player.on('pause', () => setIsPlaying(false))
  }

  const videoJsPlayerOptions = {
    controls: true,
    responsive: true,
    fluid: true,
    aspectRatio: '16:9',
    preload: 'metadata',
    autoplay: false,
    bigPlayButton: false,
  }

  // Video asset configuration
  const videoAsset = {
    playbackUrlDash:
      'https://customer-lh6w93xbrhbnz4ws.cloudflarestream.com/ca92752bdec2ffe48bce414876f3434a/manifest/video.mpd',
    playbackUrlHLS:
      'https://customer-lh6w93xbrhbnz4ws.cloudflarestream.com/ca92752bdec2ffe48bce414876f3434a/manifest/video.m3u8',
  }

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 xxl:px-16">
        <div className={`${style.container} mx-auto pt-4 pb-10 sm:pb-0 w-full`}>
          {!hideVideo && (
            <div className="relative rounded-lg overflow-hidden mb-7 xxxl:mb-20 shadow-2xl">
              <div className="aspect-[16/9] relative object-cover">
                <VideoJSPlayer
                  videoJsPlayerOptions={videoJsPlayerOptions}
                  videoAsset={videoAsset}
                  onPlayerReady={handlePlayerReady}
                />

                {!isPlaying && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="absolute inset-0 flex items-end pointer-events-none">
                      <div className="w-full mb-3 mx-1">
                        <h2 className={style.heading}>Watch a Video on How to Use Helen</h2>
                      </div>
                    </div>
                    <div
                      className={`${style.playIcon} rounded-full bg-secondary backdrop-blur-sm flex items-center justify-center cursor-pointer`}
                      onClick={handlePlayVideo}
                    >
                      <IcoMoon icon="play-full" color="white" size={isLargeDesktopAndAbove ? 80 : 50} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search Section */}
          <div className="mb-7">
            <HelenSearchSection id="HelenSearch" />
          </div>

          {/* Examples Section */}
          <div className="w-full">
            {fetching ? (
              <GsuLoader className="xxs:left-[40%] sm:left-[45%]" />
            ) : (
              data &&
              data.partnerAssistantPrompts.length > 0 && (
                <PartnerAssistantPromptsBlock id="AssistantPrompts" items={data.partnerAssistantPrompts} />
              )
            )}
          </div>
          <div className="mt-8">
            {helenClasses && helenClasses.learnHelenUseClasses.length > 0 && (
              <VODClassCardRow
                title={'Learn how to use Helen'}
                items={helenClasses.learnHelenUseClasses}
                fetching={fetchingHelenClasses}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default Search
